import { buildMultiSelectConfirmedAgentMessage } from '@fruitful/llm-service-shared';
import { type MultiSelectInputWidgetProps } from '@fruitful/types';
import React, { useCallback, useState } from 'react';
import {
    ArrowUpIcon,
    BodyMediumRegular,
    BodySmallRegular,
    H6Regular,
    Icon,
    IconButton,
    Input,
    PencilIcon,
    View,
    XStack,
    YStack,
} from 'smoothie';

import { useNumberKeySelection } from '../../hooks/useNumberKeySelection';
import { CHAT_INPUT_WIDGET } from '../shared-chat/ChatLayout';
import { BaseInputWidget } from './BaseInputWidget';

const COMPOSER_LEADING_SLOT_SIZE = 40;

interface SingleSelectWidgetProps extends MultiSelectInputWidgetProps {
    disabled?: boolean;
    isActive?: boolean;
    /** Optional gray sub-header shown under the prompt (e.g. reassurance copy). */
    promptSubtext?: string;
    onSubmit: (
        displayText: string,
        agentPayload: string,
    ) => void | Promise<void>;
}

export const SingleSelectWidget = ({
    options,
    responseKey,
    promptText,
    promptSubtext,
    allowCustomInput = false,
    customInputPlaceholder = 'Something else...',
    disabled = false,
    isActive = true,
    onSubmit,
}: SingleSelectWidgetProps) => {
    const [hasSubmitted, setHasSubmitted] = useState(false);
    const [customInputValue, setCustomInputValue] = useState('');
    const isWidgetDisabled = disabled || !isActive || hasSubmitted;

    const submit = useCallback(
        (displayText: string, optionId: string | null, inputText: string) => {
            const agentPayload = buildMultiSelectConfirmedAgentMessage({
                displayText,
                payload: {
                    responseKey,
                    selectedOptionIds: optionId != null ? [optionId] : [],
                    inputText,
                },
            });

            setHasSubmitted(true);
            void onSubmit(displayText, agentPayload);
        },
        [onSubmit, responseKey],
    );

    const handleSelect = useCallback(
        (optionId: string, label: string) => {
            if (isWidgetDisabled) return;
            submit(label, optionId, '');
        },
        [isWidgetDisabled, submit],
    );

    const handleCustomSend = useCallback(() => {
        const trimmed = customInputValue.trim();
        if (isWidgetDisabled || trimmed === '') return;
        submit(trimmed, null, trimmed);
    }, [customInputValue, isWidgetDisabled, submit]);

    useNumberKeySelection({
        enabled: !isWidgetDisabled,
        optionCount: options.length,
        onSelectIndex: (index) => {
            const option = options[index];
            if (option != null) {
                handleSelect(option.id, option.label);
            }
        },
    });

    const canSend = !isWidgetDisabled && customInputValue.trim() !== '';

    return (
        <BaseInputWidget
            alignSelf="stretch"
            width="100%"
            paddingTop={CHAT_INPUT_WIDGET.paddingTop}
            paddingBottom={
                allowCustomInput ? 0 : CHAT_INPUT_WIDGET.paddingBottom
            }
        >
            {promptText != null && (
                <YStack
                    paddingHorizontal={CHAT_INPUT_WIDGET.rowPaddingHorizontal}
                    paddingBottom={12}
                    gap={promptSubtext != null ? 4 : 0}
                >
                    <H6Regular>{promptText}</H6Regular>
                    {promptSubtext != null && (
                        <BodySmallRegular color="$gray1000">
                            {promptSubtext}
                        </BodySmallRegular>
                    )}
                </YStack>
            )}
            {options.map((option, i) => (
                <XStack
                    gap={12}
                    key={option.id}
                    alignItems="center"
                    paddingHorizontal={CHAT_INPUT_WIDGET.rowPaddingHorizontal}
                    paddingVertical={CHAT_INPUT_WIDGET.rowPaddingVertical}
                    cursor={isWidgetDisabled ? 'default' : 'pointer'}
                    pointerEvents={isWidgetDisabled ? 'none' : 'auto'}
                    onPress={() => {
                        handleSelect(option.id, option.label);
                    }}
                    pressStyle={
                        !isWidgetDisabled ? { opacity: 0.7 } : undefined
                    }
                >
                    <View
                        width={40}
                        height={40}
                        justifyContent="center"
                        alignItems="center"
                        backgroundColor="$gray200"
                        borderRadius={16}
                    >
                        <BodyMediumRegular>{i + 1}</BodyMediumRegular>
                    </View>
                    <YStack
                        flex={1}
                        minHeight={CHAT_INPUT_WIDGET.rowMinHeight}
                        justifyContent="center"
                        pointerEvents="none"
                    >
                        <BodyMediumRegular>{option.label}</BodyMediumRegular>
                    </YStack>
                </XStack>
            ))}
            {allowCustomInput && (
                <XStack
                    alignItems="center"
                    gap={CHAT_INPUT_WIDGET.rowGap}
                    minHeight={48}
                    paddingLeft={CHAT_INPUT_WIDGET.rowPaddingHorizontal}
                    paddingRight={8}
                    paddingTop={CHAT_INPUT_WIDGET.rowPaddingVertical}
                    paddingBottom={8}
                >
                    <XStack
                        width={COMPOSER_LEADING_SLOT_SIZE}
                        height={COMPOSER_LEADING_SLOT_SIZE}
                        alignItems="center"
                        justifyContent="center"
                        flexShrink={0}
                    >
                        <Icon icon={PencilIcon} size={16} color="$gray1000" />
                    </XStack>
                    <XStack
                        flex={1}
                        minHeight={CHAT_INPUT_WIDGET.rowMinHeight}
                        alignItems="center"
                    >
                        <Input
                            flex={1}
                            width="100%"
                            size="regular"
                            fontWeight="regular"
                            placeholder={customInputPlaceholder}
                            placeholderTextColor="$gray1000"
                            value={customInputValue}
                            disabled={isWidgetDisabled}
                            height={40}
                            padding={0}
                            paddingLeft={0}
                            paddingRight={8}
                            paddingVertical={0}
                            borderWidth={0}
                            backgroundColor="transparent"
                            focusStyle={{
                                borderWidth: 0,
                                outlineWidth: 0,
                            }}
                            hoverStyle={{
                                borderWidth: 0,
                                outlineWidth: 0,
                            }}
                            containerProps={{
                                flex: 1,
                                width: '100%',
                                height: 40,
                                alignItems: 'center',
                                backgroundColor: 'transparent',
                            }}
                            onChangeText={setCustomInputValue}
                            onSubmitEditing={handleCustomSend}
                        />
                    </XStack>
                    <IconButton
                        variant="primary"
                        icon={ArrowUpIcon}
                        disabled={!canSend}
                        onPress={handleCustomSend}
                    />
                </XStack>
            )}
        </BaseInputWidget>
    );
};
